import { forwardRef } from "react"
import { LexicalText } from "@/schema"
import { cn } from "@ui/utils"

import { EmailEditor } from "@/components/lexical/editors"
import { EditorRefHandle } from "@/components/lexical/editors/email"
import type { EmailEditorProvider } from "@/components/lexical/editors/email/provider-styles"
import { PlaceholderData } from "@/components/lexical/placeholders"

interface EmailBodyEditorProps {
  onChange?: (lexicalText: LexicalText) => void
  placeholder?: string
  initialContent?: LexicalText
  styleOverrides?: React.CSSProperties | Record<`--${string}`, string>
  enablePlaceholders?: boolean
  placeholders?: PlaceholderData[]
  plugins?: React.ReactNode
  className?: string
  editable?: boolean
  enableImages?: boolean
  reserveFloatingFormatBarSpace?: boolean
  provider?: EmailEditorProvider
}

const EmailBodyEditor = forwardRef<EditorRefHandle, EmailBodyEditorProps>(function EmailBodyEditor(
  {
    onChange,
    placeholder = "Tip: hit / for styles, snippets, and variables",
    initialContent,
    styleOverrides,
    enablePlaceholders = false,
    placeholders,
    plugins,
    className,
    editable = true,
    enableImages = true,
    reserveFloatingFormatBarSpace = false,
    provider,
  },
  ref
) {
  const defaultStyleOverrides = {
    "--lexical-editor-min-height": "240px",
    ...(reserveFloatingFormatBarSpace
      ? {
          "--lexical-editor-padding-bottom": "48px",
        }
      : {}),
    ...styleOverrides,
  }

  return (
    <div className={cn("px-4 pt-4", editable ? "pb-12" : "pb-4", className)}>
      <EmailEditor
        ref={ref}
        onChange={onChange}
        placeholder={placeholder}
        initialContent={initialContent}
        styleOverrides={defaultStyleOverrides}
        enablePlaceholders={enablePlaceholders}
        placeholders={placeholders}
        plugins={plugins}
        editable={editable}
        enableImages={enableImages}
        caretBottomOffset={reserveFloatingFormatBarSpace ? 48 : undefined}
        provider={provider}
      />
    </div>
  )
})

export default EmailBodyEditor
